import React, { Fragment, useState, useEffect } from 'react';
import Typography from '@material-ui/core/Typography';
import Skeleton from '@material-ui/lab/Skeleton';
import { deleteMedia, uploadMedia } from '../../services/index';
import { DELETE_USER_AVATAR } from '../../services/patient';
import getFileFromUrl from '../../utils/getFileFromUrl';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import { alertError, alertSuccess, setUser } from '../../redux/stateActions';

import _ from 'lodash';

import { Title, Tag, AvatarWithUploader, Avatar, Text } from '../index';

import { StyledCard, StyledCardContent } from './style';

export const PatientInfoCard = ({ user, avatarUploader, $avatarSize, mdUp, mobileS }) => {
    const dispatch = useDispatch();

    /*--------------- Update User Avatar ---------------*/
    const [newAvatar, setNewAvatar] = useState(null);
    const [newFileInfo, setNewFileInfo] = useState(null);

    const [deleteUserAvatar, { data: deleteUserAvatarData, error: deleteUserAvatarError }] =
        useMutation(DELETE_USER_AVATAR);

    useEffect(() => {
        if (deleteUserAvatarError) {
            dispatch(alertError(`Cannot delete user avatar: ${deleteUserAvatarError.message}`));
        }
    }, [deleteUserAvatarError, deleteUserAvatarData]);

    useEffect(() => {
        const uploadAvatar = async () => {
            const nameArr = newFileInfo.name.split('.');

            const myFile = await getFileFromUrl(
                newAvatar,
                `avatar_${user.id}.${nameArr[nameArr.length - 1]}`,
                newFileInfo.type
            );

            const formData = new FormData();
            formData.append('files', myFile);
            formData.append('ref', 'user');
            formData.append('refId', user.id);
            formData.append('field', 'avatar');
            formData.append('source', 'users-permissions');

            if (!_.isNull(user.avatar)) {
                try {
                    deleteUserAvatar({
                        variables: {
                            id: user.id,
                        },
                    });
                    await deleteMedia(user.avatar.id);
                } catch (error) {
                    console.log('deleteMedia error', error);
                }
            }

            try {
                const res = await uploadMedia(formData);

                if (!_.isEmpty(res)) {
                    setNewAvatar(null);
                    setNewFileInfo(null);
                    const updateURL = res?.[0]?.url;
                    if(updateURL){
                        dispatch(setUser({ ...user, avatar: {
                            ...user.avatar,
                            url: updateURL
                        } }));
                    }
                    dispatch(alertSuccess('Avatar Uploaded'));
                }
            } catch (error) {
                dispatch(
                    alertError(
                        `Cannot upload Avatar: ${JSON.stringify(error.response?.data?.message)}`
                    )
                );
            }
        };

        if (!_.isNull(newAvatar)) {
            uploadAvatar();
        }
    }, [newAvatar]);

    return (
        <StyledCard variant="outlined" padding="15px">
            <StyledCardContent
                style={{
                    display: 'flex',
                }}
            >
                {user ? (
                    avatarUploader ? (
                        <AvatarWithUploader
                            alt={user?.fullname}
                            src={user ? user?.avatar?.url : null}
                            $avatarSize={$avatarSize}
                            setNewAvatar={setNewAvatar}
                            setNewFileInfo={setNewFileInfo}
                            $styleButton={{
                                bottom: mdUp ? 0 : '-12px',
                                right: mdUp ? 0 : '-7px',
                                margin: mdUp ? '5px' : 0,
                            }}
                        />
                    ) : (
                        <Avatar
                            alt={user?.fullname}
                            src={user.avatar ? user?.avatar?.url : null}
                            $avatarSize={$avatarSize}
                        />
                    )
                ) : (
                    <div style={{ width: $avatarSize, height: $avatarSize }}>
                        <Skeleton
                            component="div"
                            variant="rect"
                            width={$avatarSize}
                            height={$avatarSize}
                        />
                    </div>
                )}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: mobileS ? '0 0 5px 15px' : '0 0 5px 20px',
                        justifyContent: 'space-between',
                    }}
                >
                    {user ? (
                        <Title
                            $fontSize="18px"
                            style={{ margin: '4px 0 8px' }}
                        >{`${user?.fullname}`}</Title>
                    ) : (
                        <Skeleton width={100} height={30} variant="text" />
                    )}
                    {user ? (
                        <Typography variant="h6" style={{ fontSize: '14px', fontWeight: 400 }}>
                            ID: {user?.patient?.HNCode}
                        </Typography>
                    ) : (
                        <Skeleton width={100} height={30} variant="text" />
                    )}
                    <div style={{ display: 'flex' }}>
                        {user ? (
                            user.birthday && (
                                <Typography
                                    variant="h6"
                                    style={{
                                        fontSize: '14px',
                                        fontWeight: 400,
                                        marginRight: '10px',
                                    }}
                                >
                                    Age: {dayjs().diff(dayjs(user.birthday), 'year')}
                                </Typography>
                            )
                        ) : (
                            <Skeleton width={100} height={30} variant="text" />
                        )}
                        {user ? (
                            <Typography variant="h6" style={{ fontSize: '14px', fontWeight: 400 }}>
                                Gender: {user?.gender}
                            </Typography>
                        ) : (
                            <Skeleton
                                width={100}
                                height={30}
                                variant="text"
                                style={{ marginLeft: '5px' }}
                            />
                        )}
                    </div>

                    <div style={{ display: 'flex', columnGap: '10px' }}>
                        {user?.patient?.patientTag ? (
                            user?.patient?.patientTag.map((diagnosis, index) => (
                                <Tag key={diagnosis + index}>{diagnosis}</Tag>
                            ))
                        ) : (
                            <Fragment>
                                <Skeleton width={50} height={30} variant="text" />
                                <Skeleton
                                    width={50}
                                    height={30}
                                    variant="text"
                                    style={{ marginLeft: '5px' }}
                                />
                            </Fragment>
                        )}
                    </div>
                </div>
            </StyledCardContent>
            <StyledCard
                style={{
                    padding: '8px 20px',
                    maxWidth: '450px',
                    marginTop: '25px',
                }}
            >
                <StyledCardContent style={{ display: 'flex', columnGap: '20px' }}>
                    <Avatar
                        src={user?.patient?.hospital?.avatar?.url}
                        alt={user?.patient?.hospital?.name}
                        $avatarSize="40px"
                        $borderRadius="50%"
                    />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Text $fontSize="12px" $bold>
                            Counseling Service Account
                        </Text>
                        <Text $fontSize="12px">{user?.patient?.hospital?.name}</Text>
                    </div>
                </StyledCardContent>
            </StyledCard>
        </StyledCard>
    );
};

PatientInfoCard.propTypes = {
    user: PropTypes.object,
    avatarUploader: PropTypes.bool,
    $avatarSize: PropTypes.string,
    mdUp: PropTypes.bool,
    mobileS: PropTypes.bool,
};
